import {
  Checkbox,
  Flex,
  FormGroup,
  HStack,
  MultiSelect,
  SearchSelect,
  Stack,
  Text,
  type Currency,
  type CustomerNetAcquisitionLimitConfig,
  type ICustomerNetAcquisitionLimit,
} from '@talos/kyoko';
import { identity } from 'lodash-es';
import { useCallback, useMemo } from 'react';

interface DialogContentsProps {
  currenciesBySymbol: Map<string, Currency>;
  acquisitionLimits?: ICustomerNetAcquisitionLimit[];
  config: CustomerNetAcquisitionLimitConfig;
  onConfigChange: (config: CustomerNetAcquisitionLimitConfig) => unknown;
}

export function DialogContents({ currenciesBySymbol, acquisitionLimits, config, onConfigChange }: DialogContentsProps) {
  const currencySymbols = useMemo(() => [...currenciesBySymbol.keys()], [currenciesBySymbol]);
  const getCurrencyDescription = useCallback(
    (symbol: string) => currenciesBySymbol.get(symbol)?.Description ?? '',
    [currenciesBySymbol]
  );

  const acquisitionLimitNames = useMemo(() => acquisitionLimits?.map(limit => limit.Name) ?? [], [acquisitionLimits]);

  return (
    <Flex flexDirection="column" gap="spacing8">
      <FormGroup label="Specified Assets" controlId="specified-assets">
        <Stack gap="spacing12">
          <MultiSelect
            selections={config.SpecifiedAssets ?? []}
            options={currencySymbols}
            onChange={selections => onConfigChange({ ...config, SpecifiedAssets: selections })}
            getLabel={identity}
            getDescription={getCurrencyDescription}
            data-testid="specified-assets-select"
          />
          <HStack gap="spacing16" justifyContent="start">
            <Checkbox
              data-testid="include-all-fiat-checkbox"
              checked={config.IncludeAllFiatAsSpecified}
              onChange={e => onConfigChange({ ...config, IncludeAllFiatAsSpecified: e.target.checked })}
            >
              Include all Fiat
            </Checkbox>
            <Checkbox
              data-testid="include-all-stablecoin-checkbox"
              checked={config.IncludeAllStableCoinAsSpecified}
              onChange={e => onConfigChange({ ...config, IncludeAllStableCoinAsSpecified: e.target.checked })}
            >
              Include all Stablecoin
            </Checkbox>
          </HStack>
          <Text mt="spacing4">
            Specified assets do <b>not</b> apply to Net Acquisition Limits, all other assets do.
          </Text>
        </Stack>
      </FormGroup>
      <FormGroup label="Default Limit" controlId="default-limit">
        <SearchSelect
          data-testid="default-limit-select"
          selection={config.DefaultLimit}
          options={acquisitionLimitNames}
          onChange={limit => onConfigChange({ ...config, DefaultLimit: limit })}
          getLabel={identity}
          showClear
          placeholder="No default limit"
        />
      </FormGroup>
    </Flex>
  );
}
